import React, { useState, useEffect } from 'react';
import {
  DataTable,
  DataTableColumn,
  DataType,
  DataTableOptions,
  FilterType,
  useNotify,
  UserPermission
} from '@globaleris/ui.library';
import { EditAllocation } from '.';
import { AllocationModel } from '../capacity.model';
import { useTranslation } from 'react-i18next';
import { toLocaleNumber } from '../../../helpers';
import { mapCalculatePercent } from '../capacity.mapper';
import { generateIconsFromConfiguration } from '../../../helpers/dataTableActionIconHelper';
import { ActionIcons, NonAuthRoutes } from '../../../model/enums';
import {
  filterApplicableActionsConfiguration,
  modifyAllocation,
  sendAllocations
} from '../capacity.service';
import { Edit, ListOutlined, LoupeOutlined, Pageview, Send } from '@material-ui/icons';
import {
  EnumDispatchType,
  EnumOperationState
} from '../../../api/oData/enums';
import { getCustomerDomain } from '../../../redux';
import { useHistory } from 'react-router-dom';
import moment from 'moment';
import { MenuItem, Menu, Typography } from '@material-ui/core';

import { useConfirmationDialog } from '../../../hooks/useConfirmationDialog';
import { ConfirmationDialogs } from '../../../model/enums/confirmationDialogs';
import { getUserPermissions } from '../../../helpers/permissionsHelper';
import { Pages } from '../../../model/RBAC/constants';

export interface AllocationsProps {
  title?: string | undefined;
  canEdit: boolean;
  canSend: boolean;
  data: AllocationModel[] | undefined;
}

enum EditMode {
  NOMINATION,
  ALLOCATION
}

export const Allocations: React.FC<AllocationsProps> = ({ title, canEdit, data, canSend }) => {
  const [t] = useTranslation(['capacity', 'enums']);
  const { info } = useNotify();
  let history = useHistory();
  const [allocations, setAllocations] = useState<AllocationModel[] | undefined>(data);
  const [activeAllocation, setActiveAllocation] = useState<AllocationModel | undefined>(undefined);
  const [showEditForm, setShowEditForm] = useState<boolean>(false);

  const [editable, setEditable] = useState<boolean>(canEdit);
  const [editMenuAnchorEl, setEditMenuAnchorEl] = React.useState(null);

  const { getConfirmation, closeConfirmation } = useConfirmationDialog();

  useEffect(() => {
    setEditable(canEdit);
  }, [canEdit]);

  useEffect(() => {
    setAllocations(
      data?.map(item => {
        return completeDataTableActions(item);
      })
    );
  }, [data]);

  //#region Handlers

  const onSendMassiveActionHandler = () => {
    const suitableAllocations = allocations?.filter(
      item =>
        item.operationState === EnumOperationState.PendienteRenvio ||
        item.operationState === EnumOperationState.Borrador ||
        item.operationState === EnumOperationState.Rechazado ||
        item.operationState === EnumOperationState.EnviadoKO
    );
    if (suitableAllocations === undefined || suitableAllocations.length === 0) {
      info(t('capacity:forms.allocations.notifications.info.noPendingOperationsToSend'));
    } else {
      getConfirmation(ConfirmationDialogs.SEND_MASSIVE).then(async () => {
        await sendAllocations(suitableAllocations).then(sent => {
          if (sent) {
            info(t('capacity:forms.allocations.notifications.success.sendMassive'));
          }
        });

        closeConfirmation();
      });
    }
  };

  const onFormDialogCloseHandler = async (modifiedAllocation: AllocationModel) => {
    if (modifiedAllocation !== undefined && allocations) {
      await modifyAllocation(modifiedAllocation).then(success => {
        if (success) {
          info(t('capacity:forms.allocations.notifications.success.edit'));

          const index = allocations.findIndex(x => x.id === modifiedAllocation.id);

          setAllocations(
            [
              ...allocations.slice(0, index),
              {
                ...modifiedAllocation,
                quantityView: `${toLocaleNumber(
                  modifiedAllocation.quantity
                )} (${mapCalculatePercent(
                  modifiedAllocation.quantity! / modifiedAllocation.capacityQuantity!
                )}%)`
              },
              ...allocations.slice(index + 1)
            ].map(item => {
              return completeDataTableActions(item);
            })
          );
        }
      });
    }
    setShowEditForm(false);
  };

  const onEditMenuCloseHandler = () => {
    setEditMenuAnchorEl(null);
  };

  const onEditMenuClickHandler = async (editMode: EditMode) => {
    switch (editMode) {
      case EditMode.NOMINATION: {
        history.push(NonAuthRoutes.allocationsEdit, {
          id: activeAllocation?.operationId,
          dispatchType: EnumDispatchType.NominacionConRepartos
        });
        break;
      }
      default: {
        history.push(NonAuthRoutes.allocationsEdit, {
          id: activeAllocation?.operationId,
          dispatchType: EnumDispatchType.Reparto02G
        });
        break;
      }
    }

    onEditMenuCloseHandler();
  };

  const onEditActionHandler = (row: AllocationModel, event: any) => {
    setActiveAllocation(row);
    if (moment(row.date) < moment()) {
      history.push(NonAuthRoutes.allocationsEdit, {
        id: row.operationId,
        dispatchType: EnumDispatchType.Reparto02G
      });
    } else {
      setEditMenuAnchorEl(event.currentTarget);
    }
  };

  //#endregion

  //#region DataTable configuration

  const columns = React.useMemo((): DataTableColumn[] => {
    return [
      {
        name: 'actions',
        label: '',
        hideForResponsive: true,
        type: DataType.HTML,
        columnWidth: 3
      },
      {
        name: 'date',
        label: t('capacity:forms.allocations.dataTable.columns.date'),
        type: DataType.DATE,
        columnWidth: 22
      },
      {
        name: 'quantityView',
        label: t('capacity:forms.allocations.dataTable.columns.allocatedQuantity'),
        type: DataType.TEXT,
        columnWidth: 25
      },
      {
        name: 'quantitySentView',
        label: t('capacity:forms.allocations.dataTable.columns.lastSend'),
        type: DataType.TEXT,
        columnWidth: 25
      },
      {
        name: 'quantityTSOView',
        label: t('capacity:forms.allocations.dataTable.columns.TSOInformation'),
        type: DataType.TEXT,
        columnWidth: 25
      }
    ];
  }, [t]);

  const getDataTableOptions = (): DataTableOptions => {
    return canSend
      ? {
          elevation: 1,
          print: false,
          search: false,
          filterType: FilterType.ADVANCED,
          canEdit: false,
          canDelete: false,
          canInsert: false,
          rowCustomActions: editable
            ? [
                {
                  userPermission: UserPermission.EDIT,
                  icon: <Edit fontSize="small" />,
                  visibleIf: 'canEdit',
                  tooltip: t('capacity:forms.allocations.dataTable.customActions.view'),
                  onClick: async (allocation: AllocationModel) => {
                    setShowEditForm(true);
                    setActiveAllocation(allocation);

                    return true;
                  }
                }
              ]
            : undefined,
          mainToolbarCustomActions: [
            {
              userPermission: UserPermission.SEND,
              icon: <Send fontSize="small" />,
              tooltip: t('capacity:forms.allocations.dataTable.toolbarActions.sendMassive'),
              onClick: onSendMassiveActionHandler
            }
          ],
          analytics: {
            customer: getCustomerDomain(),
            view: t('capacity:forms.allocations.dataTable.analytics')
          }
        }
      : {
          elevation: 1,
          search: false,
          print: false,
          filterType: FilterType.ADVANCED,
          canEdit: false,
          canDelete: false,
          canInsert: false,
          customInsertEditModal: false,
          analytics: {
            customer: getCustomerDomain(),
            view: t('capacity:forms.allocations.dataTable.analytics')
          },
          rowCustomActions: editable
            ? [
                {
                  userPermission: UserPermission.EDIT,
                  visibleIf: 'canEdit',
                  icon: <Pageview fontSize="small" />,
                  tooltip: t('capacity:forms.allocations.dataTable.customActions.view'),
                  onClick: onEditActionHandler
                }
              ]
            : undefined
        };
  };

  //#endregion

  const completeDataTableActions = (item: AllocationModel): AllocationModel => {
    return {
      ...item,
      actions:
        item.actionsConfiguration &&
        item.actionsConfiguration.length > 0 &&
        generateIconsFromConfiguration(
          item,
          filterApplicableActionsConfiguration(item, item.actionsConfiguration),
          new Map<ActionIcons, Function>([])
        )
    };
  };

  const editOptions = React.useMemo(() => {
    return [
      {
        label: t('capacity:forms.allocations.dataTable.customActions.editNomination'),
        icon: <LoupeOutlined />,
        mode: EditMode.NOMINATION
      },
      {
        label: t('capacity:forms.allocations.dataTable.customActions.editAllocations'),
        icon: <ListOutlined />,
        mode: EditMode.ALLOCATION
      }
    ];
  }, [t]);

  return (
    <>
      <DataTable
        title={title}
        data={allocations}
        columns={columns}
        options={getDataTableOptions()}
        userPermissions={getUserPermissions(Pages.CapacityEditBundle)}
      />
      <EditAllocation
        show={showEditForm}
        itemSelected={activeAllocation}
        onCloseForm={onFormDialogCloseHandler}
      ></EditAllocation>

      <Menu
        keepMounted={false}
        elevation={2}
        anchorEl={editMenuAnchorEl}
        open={Boolean(editMenuAnchorEl)}
        onClose={onEditMenuCloseHandler}
      >
        {editOptions.map((editOption, index) => (
          <MenuItem dense key={index} onClick={() => onEditMenuClickHandler(editOption.mode)}>
            <Typography variant="inherit">{editOption.label}</Typography>
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};
